"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Check, Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { nextAreaColor } from "@/lib/area-colors"
import type { AreaScope } from "@/lib/convex-areas"
import { useOnboardingTranslations } from "@/hooks/use-translations"

/**
 * "What competes for your time?" — the one onboarding question that changes
 * what the app can say later.
 *
 * Areas are picked here rather than left to Settings because every rollup that
 * makes Blitztask worth opening ("you are twelve hours over", "your own
 * projects got eight percent of the week") is meaningless until this list
 * exists. Nothing is written until the step is left, so backing out costs
 * nothing.
 */

export interface AreaDraft {
  name: string
  color: string
  icon: string
  scope: AreaScope
}

/**
 * Deliberately spans work *and* life: the overload this product addresses comes
 * from the mix.
 *
 * The label is a translation key, not a name — these become real area names the
 * user will read on every rollup for years, so a German speaker must get
 * "Familie", not "Family". The colour and icon are fixed across locales.
 */
const SUGGESTED_AREAS: Array<{ key: string; color: string; icon: string; scope: AreaScope }> = [
  { key: "family", color: "#ec4899", icon: "🏠", scope: "personal" },
  { key: "work", color: "#6366f1", icon: "💼", scope: "work" },
  { key: "clients", color: "#f59e0b", icon: "🤝", scope: "work" },
  { key: "my_projects", color: "#8b5cf6", icon: "🚀", scope: "personal" },
  { key: "health", color: "#10b981", icon: "🏃", scope: "personal" },
  { key: "friends", color: "#3b82f6", icon: "🍻", scope: "personal" },
  { key: "admin", color: "#64748b", icon: "📄", scope: "work" },
  { key: "study", color: "#14b8a6", icon: "📚", scope: "personal" },
]


interface AreasStepProps {
  selected: AreaDraft[]
  onChange: (next: AreaDraft[]) => void
}

export function AreasStep({ selected, onChange }: AreasStepProps) {
  const t = useOnboardingTranslations()
  const [custom, setCustom] = useState("")

  const isSelected = (name: string) =>
    selected.some((area) => area.name.toLowerCase() === name.toLowerCase())

  const toggle = (draft: AreaDraft) => {
    onChange(
      isSelected(draft.name)
        ? selected.filter((area) => area.name.toLowerCase() !== draft.name.toLowerCase())
        : [...selected, draft],
    )
  }

  const addCustom = () => {
    const name = custom.trim()
    if (!name || isSelected(name)) {
      setCustom("")
      return
    }

    onChange([
      ...selected,
      // A hand-typed area is private until the user says otherwise: this is a
      // life assistant first, and Settings can move it across in one click.
      { name, color: nextAreaColor(selected.length), icon: "✨", scope: "personal" },
    ])
    setCustom("")
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {SUGGESTED_AREAS.map((suggestion) => {
          const draft: AreaDraft = {
            name: t(`areas_step_suggestion_${suggestion.key}`),
            color: suggestion.color,
            icon: suggestion.icon,
            scope: suggestion.scope,
          }
          const active = isSelected(draft.name)
          return (
            <button
              key={suggestion.key}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(draft)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors",
                active
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-input text-muted-foreground hover:bg-muted",
              )}
            >
              <span aria-hidden>{draft.icon}</span>
              {draft.name}
              {active && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          )
        })}
      </div>

      {/* The suggestions are a prompt, not a taxonomy — most people have at
          least one demand on their time that no generic list would guess. */}
      <div className="flex gap-2">
        <Input
          value={custom}
          placeholder={t("areas_step_custom_placeholder")}
          onChange={(event) => setCustom(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              addCustom()
            }
          }}
        />
        <Button type="button" variant="outline" onClick={addCustom} disabled={!custom.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((area) => (
            <span
              key={area.name}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs"
              style={{ backgroundColor: `${area.color}20`, color: area.color }}
            >
              <span aria-hidden>{area.icon}</span>
              {area.name}
            </span>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t("areas_step_hint")}</p>
    </div>
  )
}
